import { isMobile } from "@classdojo/web";
import { DisplayText } from "@classdojo/web/nessie";
import { NessieThemeColors } from "@classdojo/web/nessie/components/theme";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { TranslatedString } from "@classdojo/web/pods/i18n/translate";
import { autoTranslate } from "@web-monorepo/vite-auto-translate-plugin/runtime";
import FocusTrap from "focus-trap-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import MaterialIcon from "./MaterialIcon";

interface AppPageProps {
  title: TranslatedString;
  icon?: string;
  backgroundColor?: NessieThemeColors;
  backButtonStyle?: ThemeUIStyleObject;
  headerStyle?: ThemeUIStyleObject;
  pageStyle?: ThemeUIStyleObject;
  classId?: string;
  mountPoint?: string;
  children: React.ReactNode;
  textColor?: string;
  titleColor?: string;
  /**
   * This prop is necessary for when rendering an AppPage when another is already rendered.
   * as this component uses focus trap, if you open a new one on top of another, the first one will prevent any kind of focus on the second one.
   */
  disableFocusTrap?: boolean;
}

const AppPage = (props: AppPageProps) => {
  const {
    title,
    icon,
    backgroundColor,
    children,
    pageStyle: customPageStyle,
    headerStyle: customHeaderStyle,
    backButtonStyle: customBackButtonStyle,
    mountPoint,
    classId,
    disableFocusTrap,
    textColor,
    titleColor,
  } = props;

  const navigate = useNavigate();
  const handleBack = () => {
    navigate(`/apps/remote/${mountPoint}/${classId}`);
  };
  return (
    <FocusTrap
      active={!isMobile.any && !disableFocusTrap}
      focusTrapOptions={{
        allowOutsideClick: true,
      }}
    >
      <article sx={{ ...pageStyle, ...customPageStyle }}>
        <header sx={{ ...headerStyle, backgroundColor, ...customHeaderStyle }}>
          <button
            data-name="app_page:material_icon"
            sx={{ ...backButtonStyle, ...customBackButtonStyle }}
            aria-label={autoTranslate("Go back")}
            onClick={handleBack}
          >
            <MaterialIcon icon="arrow_back" sx={{ color: textColor ?? "dt_content_light" }} />
          </button>
          <img src={icon} sx={{ ...iconStyle, color: textColor ?? "dt_content_light" }} alt="" />
          <DisplayText level={4} as="h1" kind="light" sx={{ color: titleColor ?? textColor ?? "dt_content_light" }}>
            {title}
          </DisplayText>
        </header>
        {children}
      </article>
    </FocusTrap>
  );
};

export default AppPage;
const pageStyle: ThemeUIStyleObject = {
  backgroundColor: "dt_background_primary",
  color: "dt_content_primary",
};
const headerStyle: ThemeUIStyleObject = {
  position: "relative",
  padding: RAW_cssValue("24px"),
  textAlign: "center",
  backgroundColor: RAW_cssValue("#c4c4c4"),
  color: "dt_content_light",
};
const backButtonStyle: ThemeUIStyleObject = {
  display: "block",
  position: "absolute",
  top: "0",
  left: "0",
  padding: RAW_cssValue("20px 10px"),
  fontSize: "24px",
  cursor: "pointer",
  border: "none",
  backgroundColor: "transparent",
};
const iconStyle: ThemeUIStyleObject = {
  maxWidth: "80px",
  height: "80px",
  margin: RAW_cssValue("0 0 15px 0"),
};
